import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { EmailIntakeService } from './email-intake.service';

@Component({
	selector: 'app-email-intake',
	templateUrl: './email-intake.component.html',
	styleUrls: ['./email-intake.component.scss'],
})
export class EmailIntakeComponent implements OnInit {
	intakes: any[] = [];
	selectedIntake: any = null;
	loading = false;
	selectedCustomer: any = null;
	selectedVendor: any = null;
	auditRows: any[] = [];
	kindsSchema: any = null;
	filterKind = '';
	filterState = '';
	reclassifyKind = '';
	editableItems: any[] = [];
	previewResult: any = null;
	confirmToken = '';
	lastPollErrors: string[] = [];
	paymentAmount = 0;
	color = 'blue';

	constructor(
		private intakeService: EmailIntakeService,
		private globalSearchService: GlobalSearchService,
		private location: Location,
		private router: Router,
	) {
		this.color = this.globalSearchService.getColor();
	}

	back(): void {
		this.location.back();
	}

	ngOnInit(): void {
		this.intakeService.getSchema().subscribe((res: any) => {
			if (res?.success) {
				this.kindsSchema = res.data;
			}
		});
		this.loadIntakes();
	}

	loadIntakes(): void {
		this.loading = true;
		this.intakeService.list(this.filterKind, this.filterState).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.intakes = res.data;
			}
		});
	}

	applyFilters(): void {
		this.loadIntakes();
	}

	selectIntake(intake: any): void {
		this.loading = true;
		this.selectedCustomer = null;
		this.selectedVendor = null;
		this.auditRows = [];
		this.previewResult = null;
		this.confirmToken = '';
		this.intakeService.get(intake.id).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.selectedIntake = res.data;
				this.reclassifyKind = res.data.intake_kind || '';
				this.editableItems = this.cloneItems(res.data.parsed_json?.items);
				this.paymentAmount = this.sumAllocations(res.data.parsed_json?.allocations);
				if (res.data.parsed_json?.customer) {
					this.selectedCustomer = res.data.parsed_json.customer;
				}
				if (res.data.parsed_json?.vendor) {
					this.selectedVendor = res.data.parsed_json.vendor;
				}
				this.loadAudit(intake.id);
			}
		});
	}

	loadAudit(intakeId: number): void {
		this.intakeService.listAudit(intakeId).subscribe((res: any) => {
			if (res?.success) {
				this.auditRows = res.data;
			}
		});
	}

	cloneItems(items: any): any[] {
		if (!Array.isArray(items)) {
			return [];
		}
		return items.map((it) => ({ ...it }));
	}

	sumAllocations(allocs: any): number {
		if (!Array.isArray(allocs)) {
			return 0;
		}
		return allocs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
	}

	bodyText(row: any): string {
		const n = row?.normalized_json;
		if (n && typeof n === 'object') {
			if (n.body_text) {
				return String(n.body_text);
			}
			if (n.body_preview) {
				return String(n.body_preview);
			}
		}
		return '';
	}

	attachments(row: any): any[] {
		const n = row?.normalized_json;
		return n && Array.isArray(n.attachments) ? n.attachments : [];
	}

	triggerPoll(): void {
		this.loading = true;
		this.intakeService.triggerPoll().subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.lastPollErrors = res.errors || [];
				let msg = res.message || 'Poll complete';
				if (this.lastPollErrors.length) {
					msg += ' (' + this.lastPollErrors.length + ' warning(s))';
				}
				this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
				this.loadIntakes();
			} else {
				this.globalSearchService.showNotification(res?.message || 'Error', 'warning', 'bottom', 'right');
			}
		});
	}

	reextract(): void {
		if (!this.selectedIntake) {
			return;
		}
		this.loading = true;
		this.intakeService.reextract(this.selectedIntake.id).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.globalSearchService.showNotification('Re-extracted with AI/rules', 'success', 'bottom', 'right');
				this.selectIntake({ id: this.selectedIntake.id });
			} else {
				this.globalSearchService.showNotification(res?.message || 'Re-extract failed', 'warning', 'bottom', 'right');
			}
		});
	}

	buildPostPayload(): any {
		const kind = String(this.selectedIntake?.intake_kind || '');
		const payload: any = {
			items: this.editableItems,
		};
		if (this.needsCustomer()) {
			payload.order_data = { customer: this.selectedCustomer, items: this.editableItems };
			payload.customer = this.selectedCustomer;
		}
		if (this.needsVendor()) {
			payload.vendor = this.selectedVendor || this.selectedIntake?.parsed_json?.vendor;
		}
		if (kind === 'payment_remittance') {
			payload.customer = this.selectedCustomer;
			payload.payment = {
				amount: this.paymentAmount,
				payment_type: 'Check',
				currency: 'USD',
			};
			payload.allocations = this.selectedIntake?.parsed_json?.allocations || [];
		}
		return payload;
	}

	previewPost(): void {
		if (!this.selectedIntake) {
			return;
		}
		if (this.needsCustomer() && !this.selectedCustomer) {
			this.globalSearchService.showNotification('Select a customer first', 'warning', 'bottom', 'right');
			return;
		}
		this.loading = true;
		this.confirmToken = '';
		this.intakeService.previewPost(this.selectedIntake.id, this.buildPostPayload()).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.previewResult = res;
				this.confirmToken = res.confirm_token || '';
				this.globalSearchService.showNotification('Preview ready — review then Commit', 'success', 'bottom', 'right');
			} else {
				this.globalSearchService.showNotification(res?.message || 'Preview failed', 'warning', 'bottom', 'right');
			}
		});
	}

	commitPost(): void {
		if (!this.selectedIntake || !this.confirmToken) {
			this.globalSearchService.showNotification('Run Preview ERP post first', 'warning', 'bottom', 'right');
			return;
		}
		this.loading = true;
		this.intakeService.commitPost(this.selectedIntake.id, this.buildPostPayload(), this.confirmToken).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				if (res.deeplink) {
					this.globalSearchService.showNotification('Posted to ERP', 'success', 'bottom', 'right');
					this.router.navigateByUrl(String(res.deeplink).replace(/^\/#/, ''));
				} else if (res.orderno) {
					this.globalSearchService.showNotification('Order created: ' + res.orderno, 'success', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification('Posted to ERP', 'success', 'bottom', 'right');
				}
				this.selectedIntake = null;
				this.previewResult = null;
				this.confirmToken = '';
				this.loadIntakes();
			} else {
				this.globalSearchService.showNotification(res?.message || 'Commit failed', 'warning', 'bottom', 'right');
			}
		});
	}

	reject(): void {
		if (!this.selectedIntake) {
			return;
		}
		this.loading = true;
		this.intakeService.reject(this.selectedIntake.id).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.globalSearchService.showNotification('Intake rejected', 'success', 'bottom', 'right');
				this.selectedIntake = null;
				this.auditRows = [];
				this.loadIntakes();
			}
		});
	}

	reclassify(newKind: string): void {
		if (!this.selectedIntake || !newKind) {
			return;
		}
		this.loading = true;
		this.intakeService.reclassify(this.selectedIntake.id, newKind).subscribe((res: any) => {
			this.loading = false;
			if (res?.success) {
				this.globalSearchService.showNotification('Classification updated', 'success', 'bottom', 'right');
				this.selectIntake({ id: this.selectedIntake.id });
				this.loadIntakes();
			}
		});
	}

	addLine(): void {
		this.editableItems.push({ stockid: '', quantity: 1, unitprice: 0 });
	}

	removeLine(i: number): void {
		this.editableItems.splice(i, 1);
	}

	showPostActions(): boolean {
		if (!this.selectedIntake) {
			return false;
		}
		const st = String(this.selectedIntake.state || '');
		return ['posted', 'rejected', 'cancelled'].indexOf(st) < 0;
	}

	needsCustomer(): boolean {
		const kind = String(this.selectedIntake?.intake_kind || '');
		return ['sales_order_email', 'customer_po_email', 'generic_operational', 'payment_remittance'].indexOf(kind) >= 0;
	}

	needsVendor(): boolean {
		const kind = String(this.selectedIntake?.intake_kind || '');
		return ['vendor_po_email', 'vendor_invoice_pdf', 'vendor_credit_memo'].indexOf(kind) >= 0;
	}

	showLineEditor(): boolean {
		const kind = String(this.selectedIntake?.intake_kind || '');
		return ['sales_order_email', 'customer_po_email', 'vendor_po_email', 'vendor_invoice_pdf', 'generic_operational'].indexOf(kind) >= 0;
	}

	openPostOrderLink(): void {
		const pr = this.selectedIntake?.post_result_json;
		if (pr && typeof pr === 'object' && pr.deeplink) {
			this.router.navigateByUrl(String(pr.deeplink).replace(/^\/#/, ''));
		}
	}

	openSettings(): void {
		this.router.navigateByUrl('/system/company?tab=mail-intake');
	}
}
